import React, { useCallback } from "react";
import { connect } from "react-redux";

import { Formik, FormikValues, FormikErrors } from "formik";
import { bindActionCreators, Dispatch } from "redux";

import { Form, PickerInput, DataColumnDef, ITextQuery, IDataColumnDefProps, ValidationMessage } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { releaseMessages } from "../../release.localization";
import { releaseDetailsManagerActions } from "../ReleaseDetails.actions";
import { IReleaseDetailsState, IMessage, IMessageData } from "../ReleaseDetails.state";

import ReleaseMessagesGrid from "./ReleaseMessagesGrid";

export interface IReleaseMessagesFormProps {
  associatedMessage: IMessageData[];
  messagesPickerData: IMessage[];
  newMessages: IMessage[];
  status: string;
  loadPickerMessages: (criterion: IPickerSearchCriterion<IMessage>) => void;
  selectNewMessages: (message: IMessage[]) => void;
}

const ReleaseMassagesForm: React.FC<IReleaseMessagesFormProps> = ({ associatedMessage, messagesPickerData, newMessages, status, loadPickerMessages, selectNewMessages }) => {
  const localizedValues = useLocalizedValues(() => ({
    messageIdTitle: displayMessages.property("messageId"),
    textTitle: displayMessages.property("text"),
    manageMessagesLabel: releaseMessages.heading("releaseMessages")
  }));

  const localizer = useLocalizer();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexReleaseManager || status !== releaseMessages.option("releaseStatusPlanning").value;

  const onNewRelationshipSelected = useCallback(
    async (rows: IMessage[], validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>, setValues: (values: FormikValues) => void) => {
      selectNewMessages(rows);
      setValues({
        newMessages: rows
      });
      await validateForm();
    },
    [selectNewMessages]
  );

  const onValidate = useCallback(
    (values: FormikValues) => {
      const errors: any = {};
      if (values.newMessages.length === 0) {
        errors.manageRelationshipsPicker = localizer(validationMessages.error("propertyIsRequired"), {
          property: localizedValues.manageMessagesLabel
        });
      }
      return errors;
    },
    [localizedValues.manageMessagesLabel, localizer]
  );

  const renderPicker = (
    errors: FormikErrors<FormikValues>,
    handleBlur: (e: React.FocusEvent<any>) => void,
    validateForm: (values?: any) => Promise<FormikErrors<FormikValues>>,
    setValues: (values: FormikValues) => void
  ) => {
    return (
      <PickerInput
        multiSelect={true}
        name="messagesPicker"
        dialogTitle={localizedValues.manageMessagesLabel}
        keySelector={(row: IMessage) => row.messageId}
        displaySelector={(row: IMessage) => row.messageId}
        selected={newMessages}
        data={messagesPickerData}
        onBlur={handleBlur}
        disabled={isReadOnlyMode}
        onSelectionChanged={rows => onNewRelationshipSelected(rows as IMessage[], validateForm, setValues)}
        onSearch={(query: ITextQuery, columns: Array<IDataColumnDefProps<IMessage>>) => loadPickerMessages({ query: query, searchedColumns: columns })}
      >
        <DataColumnDef<IMessage> key="1" id="messageId" searchable={true} sortable={true} title={localizedValues.messageIdTitle} valueSelector={row => row.messageId}>
          {(row: IMessage) => row.messageId}
        </DataColumnDef>
        <DataColumnDef<IMessage> key="2" id="text" searchable={true} sortable={true} title={localizedValues.textTitle} valueSelector={row => row.text}>
          {(row: IMessage) => row.text}
        </DataColumnDef>
      </PickerInput>
    );
  };

  return (
    <Formik
      initialValues={{
        newMessages: []
      }}
      validate={onValidate}
      onSubmit={(_: FormikValues, { setSubmitting }) => {
        setSubmitting(false);
      }}
    >
      {({ handleBlur, errors, setValues, validateForm }) => (
        <>
          <Form.Row id="messages-picker">
            <Form.FieldPair labelText={localizedValues.manageMessagesLabel}>
              {renderPicker(errors, handleBlur, validateForm, setValues)}
              <ValidationMessage message={errors.manageRelationshipsPicker ? (errors.manageRelationshipsPicker as string) : ""} />
            </Form.FieldPair>
          </Form.Row>
          {associatedMessage.length > 0 && (
            <Form.Row>
              <ReleaseMessagesGrid />
            </Form.Row>
          )}
        </>
      )}
    </Formik>
  );
};

export default connect(
  (state: { releaseDetails: IReleaseDetailsState }) => {
    return {
      newMessages: state.releaseDetails.newMessages,
      messagesPickerData: state.releaseDetails.messagesPickerData,
      notifications: state.releaseDetails.notifications,
      associatedMessage: state.releaseDetails.associatedMessages,
      status: state.releaseDetails.releaseStatus
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        selectNewMessages: releaseDetailsManagerActions.selectNewMessages,
        loadPickerMessages: releaseDetailsManagerActions.loadPickerMessages
      },
      dispatch
    )
)(ReleaseMassagesForm);
